import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  IconButton,
  Divider,
  Link,
  useTheme,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Phone,
  Email,
  LocationOn,
  ArrowUpward,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fadeInUp } from '../styles/animations';
import logo from '../Logo.png';
import DataService from '../Data/Services.json';

const ModernFooter: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const socialLinks = [
    { icon: <Facebook />, url: 'https://www.facebook.com/profile.php?id=61579415502548' },
    { icon: <Twitter />, url: '#' },
    { icon: <LinkedIn />, url: '#' },
    { icon: <Instagram />, url: 'https://www.instagram.com/nkh_2023/' },
  ];
  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? '#0a0e27' : '#003d7a',
        color: 'white',
        py: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <motion.div variants={fadeInUp} initial="initial" whileInView="animate">
              <Box sx={{ mb: 3 }}>
                <Box
                  component="img"
                  src={logo}
                  alt="NKH Construction"
                  sx={{
                    width: 120,
                    height: 'auto',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t("contenu.texte1")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.6, mb: 3 }}>
                {t("contenu.texte14")}
              </Typography>
              
              {/* Social Links */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {socialLinks.map((social, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                      }}
                      onClick={() => window.open(social.url, '_blank', 'noopener,noreferrer')}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Services */}
          <Grid item xs={12} md={4}>
            <motion.div variants={fadeInUp} initial="initial" whileInView="animate">
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t("contenu.texte23")}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {DataService.map((service, index) => (
                  <Box
                    key={index}
                    component="button"
                    onClick={() => handleNavigation('/services')}
                    sx={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      textAlign: 'left',
                      cursor: 'pointer',
                      opacity: 0.8,
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      '&:hover': {
                        opacity: 1,
                        transform: 'translateX(5px)',
                      },
                    }}
                  >
                    <Typography variant="body2">
                      {service.title.split('|')[i18n.language.toLowerCase() === 'fr' ? 0 : 1]}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={4}>
            <motion.div variants={fadeInUp} initial="initial" whileInView="animate">
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t("contenu.texte24")}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Phone sx={{ fontSize: 20, color: '#ff6b35' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t("contenu.texte18")}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Email sx={{ fontSize: 20, color: '#ff6b35' }} />
                  <Link
                    href="mailto:contact@nkhconstruction.com"
                    sx={{
                      color: 'white',
                      textDecoration: 'none',
                      opacity: 0.8,
                      '&:hover': {
                        opacity: 1,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    <Typography variant="body2">
                      contact@nkhconstruction.com
                    </Typography>
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocationOn sx={{ fontSize: 20, color: '#ff6b35' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t("contenu.texte20")}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocationOn sx={{ fontSize: 20, color: '#ff6b35' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t("contenu.texte21")}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

        {/* Bottom Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <motion.div variants={fadeInUp} initial="initial" whileInView="animate">
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {t("contenu.texte25")}{' '}
              <Link
                href={t("lienPartenaire")}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#ff6b35',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t("nomPartenaire")}
              </Link>
            </Typography>
          </motion.div>

          <motion.div variants={fadeInUp} initial="initial" whileInView="animate">
            <IconButton
              onClick={scrollToTop}
              sx={{
                color: 'white',
                backgroundColor: '#ff6b35',
                '&:hover': {
                  backgroundColor: '#ff8558',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <ArrowUpward />
            </IconButton>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default ModernFooter;
